<template>
  <SectionListText02 :title="t('title')" :items="items" />
</template>

<script setup lang="ts">
  import type { IItem } from '@/components/Shared/Section/List/Text02.vue';

  const { t } = useI18n({ useScope: 'local' });

  const highlight = (key: string) =>
    t(key).replace(
      /\[\[(.+?)\]\]/g,
      '<span class="font-bold text-brand-400">$1</span>'
    );

  const items = computed((): IItem[] =>
    [1, 2, 3].map((index) => ({
      name: t(`item-${index}-title`),
      descriptionHtml: highlight(`item-${index}-description`),
    }))
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-jp": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-tw": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-kr": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-americas": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-asia-pacific": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "en-emea": {
    "title": "Why Infrastructure Must Evolve",
    "item-1-title": "Dispersed Assets & Data Silos",
    "item-1-description": "Equipment is deployed across multiple locations, but operational and energy data remains siloed—[[limiting unified visibility and informed decision-making]].",
    "item-2-title": "Rising Operational Complexity",
    "item-2-description": "Limited man power, [[inconsistent processes]], and increasing response demands make it difficult to maintain stable operations and react quickly to issues across sites.",
    "item-3-title": "Higher Performance Expectations",
    "item-3-description": "[[Unplanned downtime]], energy cost volatility, and sustainability targets require more proactive insight into asset health and energy usage."
  },
  "ja-jp": {
    "title": "現代インフラ変革の要因",
    "item-1-title": "分散した資産とデータのサイロ化",
    "item-1-description": "設備が複数拠点に分散し、運用・エネルギーデータが統合されていないことにより、[[全体の可視性が不足]]し、意思決定の効率に影響を与えています。",
    "item-2-title": "運用の複雑化",
    "item-2-description": "人材不足や[[プロセスの標準化不足]]に加え、対応要求の増加により、安定運用の維持と迅速な対応が困難になっています。",
    "item-3-title": "性能・効率への要求増大",
    "item-3-description": "[[突発的なダウンタイム]]やエネルギーコストの変動、サステナビリティ要件への対応により、より高度な可視化と予測が求められています。"
  },
  "zh-tw": {
    "title": "現代基礎設施轉型的關鍵驅動力",
    "item-1-title": "資產分散與數據孤島",
    "item-1-description": "設備分布於多個場域，營運與能源數據尚未整合，導致[[整體可視性不足]]，進而影響決策效率與管理品質",
    "item-2-title": "營運複雜度提升",
    "item-2-description": "人力資源有限、[[流程標準化不足]]，加上跨據點的應變需求持續增加，使得維持穩定運作與快速因應突發狀況愈發困難",
    "item-3-title": "更高的效能與效率要求",
    "item-3-description": "[[突發停機]]、能源成本波動與永續目標，促使企業需要更前瞻的設備與能源洞察"
  }
}
</i18n>
